// ── Contractor Rob Flooring — WarrantyPage.js ──────────────
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Download } from 'lucide-react';

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    els.forEach(el => el.setAttribute('data-reveal-init', ''));
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.1 }
    );
    els.forEach(el => io.observe(el));
    const safety = setTimeout(() => els.forEach(el => el.classList.add('in')), 1500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);
}

export default function WarrantyPage() {
  useReveal();
  return (
    <main style={{ paddingTop: 76 }}>
      <div id="refresh-top" />

      {/* Header */}
      <section style={{ background: 'var(--ivory-soft)', padding: '56px 0 40px', borderBottom: '1px solid var(--rule)' }}>
        <div className="page-container">
          <Link
            to="/"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--ash)', letterSpacing: '.08em', textDecoration: 'none', marginBottom: 24 }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--ash)'}
          >
            <ArrowLeft size={12} /> Back to Home
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(232,179,57,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={18} style={{ color: 'var(--saffron-deep)' }} />
            </div>
            <p className="eyebrow eyebrow-gold">Technical Documents</p>
          </div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(36px,6vw,64px)', lineHeight: 1.0, textWrap: 'balance' }}>
            LVP Flooring Warranty
          </h1>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ash)', marginTop: 12 }}>
            Love3 Products, Inc. · Non-Transferable · Original Purchaser Only
          </p>
          {/* Download button */}
          <div style={{ marginTop: 20 }}>
            <a
              href="/lvp-warranty.pdf"
              download="LVP-Warranty-ContractorRob.pdf"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
                letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--ink)', background: 'var(--saffron)',
                padding: '10px 20px', borderRadius: 'var(--r-sm)',
                textDecoration: 'none', transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--saffron-deep)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--saffron)'}
            >
              <Download size={13} /> Download as PDF
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: '64px 0 96px' }}>
        <div className="page-container" style={{ maxWidth: 820 }}>

          {/* Important Notice */}
          <div style={{ padding: '18px 22px', background: 'rgba(232,179,57,.08)', borderRadius: 'var(--r-sm)', border: '1px solid var(--saffron)', marginBottom: 40 }} data-reveal>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>Important</p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.65 }}>
              Love3 Products, Inc. warrants that LVP flooring is free of visual defects for the period stated in this warranty. Customer or installer should carefully inspect each plank before installation. The warranty does not cover any claims related to visible defects once the floor has been installed. Love3 Products, Inc. reserves the right to verify defects by requesting samples for technical analysis or inspection.
            </p>
          </div>

          {/* What Is Covered */}
          <div data-reveal>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 32, color: 'var(--ink)', marginBottom: 28, paddingBottom: 12, borderBottom: '1px solid var(--rule)' }}>What Is Covered</h2>

            <div style={{ marginBottom: 28 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--saffron-deep)', marginBottom: 8 }}>A. Manufacture Defects</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.7 }}>
                Love3 Products, Inc. warrants the flooring does not delaminate and is free from manufacture defects including dimension, thickness, and plank stability, following ASTM F3261.
              </p>
            </div>

            <div style={{ marginBottom: 28 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--saffron-deep)', marginBottom: 8 }}>B. Wear Warranty</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.7, marginBottom: 16 }}>
                Love3 Products, Inc. warrants that for the period stated in the table below, the wear layer will not wear through. Scratches, indentations, or reduction in gloss level is not considered wear-through. Wear-through is defined as damage due to light to moderate foot traffic penetrating to the pattern layer of the product.
              </p>
              {/* Wear warranty table */}
              <div style={{ overflowX: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--sans)', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: 'var(--ink)', color: '#fff' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Wear Layer Thickness</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>0.5mm – 1mm</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>0.3 – 0.4mm</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>0.2mm</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: 'var(--ivory-soft)', borderBottom: '1px solid var(--rule)' }}>
                      <td style={{ padding: '12px 16px', color: 'var(--ash)', fontWeight: 500 }}>Residential</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--saffron-deep)', fontSize: 15 }}>25 years</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--saffron-deep)', fontSize: 15 }}>10 years</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--saffron-deep)', fontSize: 15 }}>5 years</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 16px', color: 'var(--ash)', fontWeight: 500 }}>Commercial</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--ink)', fontSize: 15 }}>10 years</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--ink)', fontSize: 15 }}>5 years</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: 'var(--ink)', fontSize: 15 }}>3 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--saffron-deep)', marginBottom: 8 }}>C. Waterproof</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.7 }}>
                Luxury Vinyl planks are waterproof and will not buckle or lose integrity when exposed to normal household spills (water, wine, cleanser without harsh chemicals). Household spills must be cleaned up promptly to prevent mold and mildew.
              </p>
            </div>

            <div style={{ marginBottom: 40 }}>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--saffron-deep)', marginBottom: 8 }}>D. Installation</p>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.7, marginBottom: 12 }}>
                Planks must be installed properly in accordance with Love3 Products, Inc. installation instructions.
              </p>
              <ul style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.8, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li>Humidity from 30%–55%, temperature 15°C–30°C, pH 7–9; subfloor flatness under 4.8mm (3/16") within 3m (9.86ft).</li>
                <li>12mm (15/32") expansion space along walls and fixed objects required for temperature expansion.</li>
                <li>Do not fix the planks with nails or glue.</li>
                <li>Room length must not exceed 10m (32.8ft).</li>
              </ul>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--ash)', marginTop: 12, fontStyle: 'italic' }}>Any problem related to installation failures is not covered by this warranty.</p>
            </div>
          </div>

          {/* What Is NOT Covered */}
          <div data-reveal>
            <h2 style={{ fontFamily: 'var(--serif)', fontSize: 32, color: 'var(--ink)', marginBottom: 28, paddingBottom: 12, borderBottom: '1px solid var(--rule)' }}>What Is Not Covered</h2>
            <ol style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.85, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li>Any visible manufacture defects after installation (dimension, thickness, colors, etc.)</li>
              <li>Damage caused by accidents, fire, burns, or flooding</li>
              <li>Indentation, scratch, and click joint damage by pets, high heels, golf shoes, rolling loads, wheelchairs, or other furniture without proper floor protectors</li>
              <li>Scratches from moving heavy appliances without felt pad or plywood panel</li>
              <li>Loss of finish gloss or color over time or from direct sunlight exposure</li>
              <li>Damage due to improper installation, including non-flat or improperly treated subfloor, insufficient acclimation (less than 48 hours), use of non-recommended sealers or hammers, or fixing planks with nails</li>
              <li>Flooring installed on a concrete subfloor without a 6-mil moisture barrier (not required for wood subfloors)</li>
              <li>Installation over radiant floor heating with surface temperature exceeding 85°F (29.8°C)</li>
              <li>Damage caused by additional underlayment on pre-attached flooring or unsuitable subfloor</li>
              <li>Chips, warps, buckles, or cracks due to insufficient expansion spaces, unstable subfloor, improper acclimation, or poor maintenance</li>
              <li>Problems due to excessive moisture, mildew, alkaline substances, or hydrostatic pressure</li>
              <li>Damage caused by non-recommended floor care products or strong chemicals</li>
              <li>Flooring installed outdoors or in areas frequently exposed to water (e.g., swimming pools, bathrooms)</li>
              <li>Installing vinyl planks with thickness/dimensions not suitable for the intended use (e.g., core thickness 3.5–4.5mm not recommended for high-traffic public areas)</li>
            </ol>
          </div>

          {/* Claims */}
          <div style={{ marginTop: 48, padding: '22px 26px', background: 'var(--ivory-soft)', borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }} data-reveal>
            <p style={{ fontFamily: 'var(--serif)', fontSize: 18, color: 'var(--ink)', marginBottom: 8 }}>Filing a Claim</p>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.65 }}>
              To file a claim, notify your Love3 Products, Inc. representative with a full description of the problem accompanied by photos and videos. Love3 Products, Inc. reserves the right to verify defects by requesting samples for technical analysis or inspection. In the case of confirmed manufacture defects, Love3 Products, Inc. will replace the corresponding materials.
            </p>
          </div>

          {/* Navigation between docs */}
          <div style={{ marginTop: 64, display: 'flex', gap: 16, flexWrap: 'wrap' }} data-reveal>
            <Link to="/technical/data-sheet" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Technical Data Sheet →
            </Link>
            <Link to="/technical/installation-instructions" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Installation Instructions →
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
